<template>
  <header
    :class="headerClasses"
    role="navigation"
    aria-label="主导航"
  >
    <div class="app-header__topline"></div>

    <div class="container-responsive app-header__bar">
      <!-- Logo -->
      <router-link to="/" class="app-logo" @click="drawerVisible = false">
        <div class="app-logo__icon">
          <i class="fab fa-tiktok tiktok-icon"></i>
        </div>
        <span class="app-logo__text">114TK</span>
      </router-link>

      <!-- Desktop nav -->
      <el-menu
        mode="horizontal"
        :ellipsis="false"
        :default-active="activeIndex"
        class="app-menu"
        @select="handleSelect"
        aria-label="桌面导航菜单"
      >
        <el-menu-item v-for="item in navItems" :key="item.index" :index="item.index">
          <i :class="item.icon" class="mr-2" aria-hidden="true"></i>
          <span>{{ item.label }}</span>
        </el-menu-item>
      </el-menu>

      <!-- Actions -->
      <div class="app-header__actions">
        <el-button
          class="app-action-btn app-action-btn--menu"
          circle
          text
          @click="drawerVisible = true"
          title="打开菜单"
        >
          <el-icon><Menu /></el-icon>
        </el-button>
      </div>
    </div>

    <!-- Mobile drawer nav -->
    <el-drawer
      v-model="drawerVisible"
      direction="ltr"
      size="80%"
      :with-header="false"
      append-to-body
      class="app-mobile-drawer"
    >
      <div class="app-mobile-drawer__header">
        <router-link to="/" class="app-mobile-drawer__brand" @click="drawerVisible = false">
          <i class="fab fa-tiktok tiktok-icon mr-2"></i>
          <span>114TK工具站</span>
        </router-link>

      </div>

      <el-menu
        :default-active="activeIndex"
        class="app-mobile-menu"
        @select="handleSelect"
      >
        <el-menu-item v-for="item in navItems" :key="item.index" :index="item.index">
          <i :class="item.icon" class="mr-3"></i>
          <span>{{ item.label }}</span>
        </el-menu-item>
      </el-menu>
    </el-drawer>
  </header>
</template>

<script>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

export default {
  name: 'AppHeader',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const drawerVisible = ref(false)

    const navItems = [
      { index: '/', label: '首页', icon: 'fas fa-home' },
      { index: '/#core-features', label: '工具', icon: 'fas fa-tools' },
      { index: '/announcements', label: '公告', icon: 'fas fa-bullhorn' },
      { index: '/partners', label: '合作伙伴', icon: 'fas fa-handshake' },
      { index: '/about', label: '关于我们', icon: 'fas fa-info-circle' }
    ]

    const toolPaths = [
      '/user-query',
      '/video-analysis',
      '/toolbox',
      '/totp-verify',
      '/qrcode-scan',
      '/ip-detection',
      '/proxy-check',
      '/proxy-tk-region',
      '/mail-tool',
      '/dashboard'
    ]

    const activeIndex = computed(() => {
      if (route.path === '/' && route.hash === '#core-features') return '/#core-features'
      if (toolPaths.some((p) => route.path.startsWith(p))) return '/#core-features'
      if (route.path.startsWith('/partners')) return '/partners'
      return route.path || '/'
    })

    const scrollToCoreFeatures = () => {
      const element = document.getElementById('core-features')
      if (!element) return
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const handleSelect = async (index) => {
      drawerVisible.value = false

      if (index === '/#core-features') {
        // 已经在工具区域 hash 上时，再次点击需要手动滚动
        if (route.path === '/' && route.hash === '#core-features') {
          scrollToCoreFeatures()
          return
        }

        await router.push({ path: '/', hash: '#core-features' })
        return
      }

      await router.push(index)
    }

    watch(
      () => route.fullPath,
      () => {
        drawerVisible.value = false
      }
    )

    // 滚动收缩效果
    const isScrolled = ref(false)
    const onScroll = () => {
      isScrolled.value = window.scrollY > 16
    }

    onMounted(() => {
      onScroll()
      window.addEventListener('scroll', onScroll, { passive: true })
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', onScroll)
    })

    const headerClasses = computed(() => [
      'app-header',
      'is-light',
      isScrolled.value ? 'is-scrolled' : ''
    ])

    return {
      navItems,
      activeIndex,
      drawerVisible,
      handleSelect,
      headerClasses
    }
  }
}
</script>

<style scoped>
.app-header {
  position: sticky;
  top: 0;
  z-index: 50;
  padding-top: var(--safe-area-top);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border-bottom: 1px solid var(--el-border-color-lighter);
  background: rgba(255, 255, 255, 0.88);
  /* 只过渡背景和阴影，避免 resize 时触发所有属性过渡 */
  transition: background-color 0.24s ease, box-shadow 0.24s ease;
}

.app-header.is-scrolled {
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.app-header__topline {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--el-color-primary), transparent);
  opacity: 0.7;
}

.app-header__bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
}

.app-logo {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 800;
  color: var(--slate-900);
  user-select: none;
}

.app-logo__icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #161823;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.3);
}

.app-logo__text {
  font-size: 18px;
  letter-spacing: 0.5px;
}

.app-cta {
  border-radius: 14px;
  background: linear-gradient(135deg, var(--brand-500), #ff6686);
  border: none;
  color: #fff;
  padding: 0 16px;
  box-shadow: 0 10px 24px rgba(255, 59, 97, 0.28);
}

.app-cta:hover {
  filter: brightness(1.05);
}

.app-header__actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.app-action-btn {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  background: var(--el-fill-color-light);
}

.app-header.is-light .app-action-btn {
  background: rgba(148, 163, 184, 0.12);
}

/* Desktop menu overrides */
:deep(.app-menu) {
  border-bottom: none;
  background: transparent;
  flex: 1;
  justify-content: center;
  min-width: 0;
}

:deep(.app-menu .el-menu-item) {
  border-bottom: none !important;
  border-radius: 14px;
  margin: 0 4px;
  height: 42px;
  line-height: 42px;
  padding: 0 14px;
  color: var(--slate-700);
  background: transparent;
  transition: background 0.2s ease, color 0.2s ease;
  font-size: 16px;
}

:deep(.app-menu .el-menu-item:hover) {
  background: var(--el-fill-color-light);
}

:deep(.app-menu .el-menu-item.is-active) {
  color: var(--el-color-primary);
  background: var(--el-fill-color-light);
}

/* Drawer styling */
:deep(.app-mobile-drawer .el-drawer__body) {
  padding: 16px;
  padding-top: calc(16px + var(--safe-area-top));
}

.app-mobile-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.app-mobile-drawer__brand {
  display: inline-flex;
  align-items: center;
  font-weight: 800;
  color: var(--el-text-color-primary);
}

:deep(.app-mobile-menu) {
  border-right: none;
}

:deep(.app-mobile-menu .el-menu-item) {
  border-radius: 14px;
  margin: 4px 0;
  height: 46px;
  line-height: 46px;
}

/* 响应式：移动端用抽屉，桌面端展示横向菜单 */
.app-menu {
  display: none !important;
}

.app-action-btn--menu {
  display: inline-flex !important;
}

@media (min-width: 768px) {
  .app-menu {
    display: flex !important;
  }

  .app-action-btn--menu {
    display: none !important;
  }

  .app-mobile-drawer {
    display: none !important;
  }
}
.tiktok-icon {
  color: #fff;
  filter: drop-shadow(-2px 0 #25F4EE) drop-shadow(2px 0 #FE2C55);
}
</style>
